(() => {
  if (!customElements.get('ag-omni-features')) {
    customElements.define('ag-omni-features', class extends HTMLElement {
      connectedCallback() {
        if (this.rail) return;
        this.rail = this.querySelector('.ag-omni-rail');
        this.cards = [...this.querySelectorAll('.ag-omni-card')];
        this.pagination = this.querySelector('.ag-omni-pagination');
        this.buttons = [...this.pagination.querySelectorAll('button')];
        this.active = 0;
        this.select = index => {
          this.active = index;
          const card = this.cards[index];
          const left = card.offsetLeft - this.cards[0].offsetLeft;
          this.rail.scrollTo({left, behavior: 'instant'});
          this.buttons.forEach((button, i) => {
            if (i === index) button.setAttribute('aria-current', 'true');
            else button.removeAttribute('aria-current');
          });
        };
        this.onClick = event => {
          const index = this.buttons.indexOf(event.target.closest('button'));
          if (index >= 0) this.select(index);
        };
        this.onKey = event => {
          if (this.pagination.hidden) return;
          const targets = {ArrowRight: Math.min(this.active + 1, this.cards.length - 1), ArrowLeft: Math.max(this.active - 1, 0), Home: 0, End: this.cards.length - 1};
          if (!(event.key in targets)) return;
          event.preventDefault();
          this.select(targets[event.key]);
        };
        this.onScroll = () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => {
            const origin = this.cards[0].offsetLeft;
            this.active = this.cards.reduce((nearest, card, index) => Math.abs(card.offsetLeft - origin - this.rail.scrollLeft) < Math.abs(this.cards[nearest].offsetLeft - origin - this.rail.scrollLeft) ? index : nearest, 0);
            this.buttons.forEach((button, index) => {
              if (index === this.active) button.setAttribute('aria-current', 'true');
              else button.removeAttribute('aria-current');
            });
          });
        };
        this.observer = new ResizeObserver(() => {
          const isScrollable = this.rail.scrollWidth > this.rail.clientWidth + 1;
          this.pagination.hidden = !isScrollable;
          this.rail.tabIndex = isScrollable ? 0 : -1;
          if (isScrollable) this.rail.scrollTo({left: this.cards[this.active].offsetLeft - this.cards[0].offsetLeft, behavior: 'instant'});
        });
        this.observer.observe(this.rail);
        this.pagination.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKey);
        this.rail.addEventListener('scroll', this.onScroll, {passive: true});
      }
      disconnectedCallback() {
        this.observer?.disconnect();
        cancelAnimationFrame(this.frame);
        this.pagination?.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKey);
        this.rail?.removeEventListener('scroll', this.onScroll);
        this.rail = null;
      }
    });
  }
  if (!customElements.get('ag-product-video')) {
    customElements.define('ag-product-video', class extends HTMLElement {
      connectedCallback() {
        this.start = () => this.load();
        this.close = () => this.reset();
        this.querySelector('.ag-video-play')?.addEventListener('click', this.start);
        this.resetButton = this.parentElement.querySelector('.ag-video-reset');
        this.resetButton?.addEventListener('click', this.close);
        if (this.hasAttribute('data-autoplay') && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting) this.load(true);
              else if (this.media?.tagName === 'VIDEO') this.media.pause();
            });
          }, {threshold: 0.4});
          this.observer.observe(this);
        }
      }
      async load(autoplay = false) {
        if (this.media) {
          if (autoplay && this.media.tagName === 'VIDEO') this.media.play().catch(() => {});
          return;
        }
        const media = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
        if (!media) return;
        this.querySelector('.ag-video-error').hidden = true;
        this.media = media;
        if (media.tagName === 'IFRAME') {
          const url = new URL(media.src);
          url.searchParams.set('autoplay', '1');
          media.src = url.href;
        }
        this.append(media);
        this.setAttribute('data-loaded', '');
        this.resetButton.hidden = false;
        if (!autoplay) media.focus();
        if (media.tagName === 'VIDEO') {
          media.addEventListener('error', () => this.reset(true), {once: true});
          try { await media.play(); } catch { /* Native controls remain available when playback requires another tap. */ }
        }
      }
      reset(error = false) {
        this.media?.pause?.();
        this.media?.remove();
        this.media = null;
        this.removeAttribute('data-loaded');
        if (this.resetButton) this.resetButton.hidden = true;
        this.querySelector('.ag-video-error').hidden = !error;
        this.querySelector('.ag-video-play')?.focus();
      }
      disconnectedCallback() {
        this.observer?.disconnect();
        this.media?.pause?.();
        this.querySelector('.ag-video-play')?.removeEventListener('click', this.start);
        this.resetButton?.removeEventListener('click', this.close);
      }
    });
  }
  if (!customElements.get('ag-feature-tabs')) {
    customElements.define('ag-feature-tabs', class extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[role="tab"]')];
        this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
        this.activate = index => {
          this.tabs.forEach((tab, i) => {
            tab.setAttribute('aria-selected', String(i === index));
            tab.tabIndex = i === index ? 0 : -1;
            this.panels[i].hidden = i !== index;
          });
        };
        this.handleClick = event => {
          const index = this.tabs.indexOf(event.target.closest('[role="tab"]'));
          if (index >= 0) this.activate(index);
        };
        this.handleKey = event => {
          const current = this.tabs.indexOf(event.target);
          if (current < 0) return;
          const count = this.tabs.length;
          const targets = {ArrowRight: (current + 1) % count, ArrowLeft: (current + count - 1) % count, Home: 0, End: count - 1};
          if (!(event.key in targets)) return;
          event.preventDefault();
          this.activate(targets[event.key]);
          this.tabs[targets[event.key]].focus();
        };
        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKey);
      }
      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keydown', this.handleKey);
      }
    });
  }
  if (!customElements.get('ag-product-scenarios')) {
    customElements.define('ag-product-scenarios', class extends HTMLElement {
      connectedCallback() {
        this.handleClick = event => {
          const summary = event.target.closest('summary');
          if (summary?.parentElement.open && matchMedia('(min-width: 700px)').matches) event.preventDefault();
        };
        this.handleToggle = event => {
          if (event.target.tagName !== 'DETAILS' || !event.target.open) return;
          this.querySelectorAll('details[open]').forEach(detail => {
            if (detail !== event.target) detail.open = false;
          });
        };
        this.addEventListener('click', this.handleClick);
        this.addEventListener('toggle', this.handleToggle, true);
      }
      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('toggle', this.handleToggle, true);
      }
    });
  }
})();
